import { CircleCheckSolidIcon } from "@elixir/icons";
import React, { useCallback, useEffect } from "react";
import { InputField } from "ui";
import usePostCreateHpr from "../../../apis/axon/hpr/use-post-create-hpr";
import useHprHfrOnboardingStore from "../../../stores/hpr-hfr-dhis-onboarding";
import { OnboardingNavigationStates } from "../../../stores/hpr-hfr-dhis-onboarding/enums";
import { onHprCreatedSuccessfully } from "../../../utils/bridges/methods";
import VerifyFlowContainer from "../verify-flow-container";
import { isHprIdAvailable } from "apis";
import { track } from "../../../utils/tracking";
import { HFR_HPR_MIXPANEL_EVENTS } from "@elixir/types";

const HprIdSuggestion = ({
  text,
  onClick,
}: {
  text: string;
  onClick: (hprId: string) => void;
}) => {
  return (
    <div
      className="dr-w-fit dr-px-8 dr-py-4 dr-rounded-4 dr-bg-darwin-touch-primary-bg-light dr-text-darwin-touch-primary-primary dr-cursor-pointer"
      onClick={() => onClick(text)}
    >
      {text}
    </div>
  );
};

const SetupYourHprId = () => {
  const [errorMessageHpr, setErrorMessageHpr] = React.useState<string>("");
  const [errorMessageEmail, setErrorMessageEmail] = React.useState<string>("");

  useEffect(() => {
    track(HFR_HPR_MIXPANEL_EVENTS.CREATE_HPR_PAGE_VIEW);
  }, []);

  const transactionId = useHprHfrOnboardingStore(
    (state) => state.transactionId
  );
  const doctorId = useHprHfrOnboardingStore(
    (state) => state.doctorId
  );
  const suggestedHprIds = useHprHfrOnboardingStore(
    (state) => state.suggestedHprIds
  );
  const setActiveScreen = useHprHfrOnboardingStore(
    (state) => state.setActiveScreen
  );
  const setCreatedHprId = useHprHfrOnboardingStore(
    (state) => state.setCreatedHprId
  );

  const [hprId, setHprId] = React.useState(() => suggestedHprIds?.[0] || "");
  const [email, setEmail] = React.useState(() => "");

  const handleHprClick = (id: string) => {
    setHprId(id);
    setErrorMessageEmail("");
  };

  const { mutateAsync: postHpr, isLoading } = usePostCreateHpr();

  const checkIfHprIdAvailable = useCallback(
    async ({ hprId }: { hprId: string }) => {
      const { data: hprAvailabilityData } = await isHprIdAvailable({
        hprUsername: hprId,
      });

      if (hprAvailabilityData && !hprAvailabilityData.available) {
        setErrorMessageEmail("This HPR Id is not available");
        return false;
      }

      return true;
    },
    []
  );

  const handleCreateHpr = async () => {
    if (hprId && email && transactionId) {
      const isHprIdAvailable = checkIfHprIdAvailable({ hprId });
      if (!isHprIdAvailable) {
        track("HPR_HPRID_UNAVAILABLE", { hprId });
        return;
      }

      if (!doctorId) {
        setErrorMessageEmail("Doctor not selected. Please go back and select a doctor.");
        return;
      }
      const { data, success, error } = await postHpr({
        hprId,
        txnId: transactionId,
        doctor_oid: doctorId,
        email,
      });

      track(HFR_HPR_MIXPANEL_EVENTS.CREATE_HPR, {
        success: `${success}`,
        error: `${error?.actualMessage || error?.message}`,
      });

      if (data && data.hprId) {
        setCreatedHprId(data.hprId);
        onHprCreatedSuccessfully();
        setActiveScreen(OnboardingNavigationStates.HPR_ID_SUCCESSFUL);
      } else {
        setErrorMessageEmail("Creation failed, please try again");
      }
    }else {
      if (!hprId) {
        setErrorMessageHpr("Enter HPR ID to proceed");
        return;
      }

      if (!email) {
        setErrorMessageEmail("Enter email to proceed");
        return;
      }
    }
  };

  return (
    <VerifyFlowContainer
      title="Setup your HPR ID"
      primaryButtonText="Continue"
      primaryButtonClick={handleCreateHpr}
      primaryButtonLoading={isLoading}
    >
      <div className="dr-py-12 dr-space-y-4">
        <InputField
          label={"Select HPR ID"}
          value={hprId}
          onChange={(e) => {
            setHprId(e.target.value);
            setErrorMessageHpr("");
          }}
          onBlur={() => {}}
          customSuffix={
            <div className="dr-bg-darwin-touch-neutral-50 dr-text-darwin-touch-neutral-1000 dr-space-x-1 BodyRegular dr-px-12 dr-py-10 dr-rounded-r-8">
              @hpr.abdm
            </div>
          }
          isMandatory={true}
          error={errorMessageHpr}
        />

        <div className="dr-flex dr-space-x-8 dr-items-center">
          <CircleCheckSolidIcon className="dr-w-14 dr-h-14 dr-text-darwin-touch-green-primary" />
          <div className="FootnoteRegular dr-text-darwin-touch-neutral-600">
            HPR ID available
          </div>
        </div>

        <InputField
            value={email}
            label={"Enter your email"}
            onChange={(e) => {
              setEmail(e.target.value);
              setErrorMessageEmail("");
            }}
            onBlur={() => {}}
            error={errorMessageEmail}
            isMandatory={true}
            placeholder={"eg: example@domain.com"}
        />

      </div>
      {suggestedHprIds ? (
        <div className="dr-flex dr-space-x-8 dr-items-center !dr-mt-4 dr-pb-4">
          {suggestedHprIds?.map((s) => (
            <HprIdSuggestion key={s} text={s} onClick={handleHprClick} />
          ))}
        </div>
      ) : null}
    </VerifyFlowContainer>
  );
};

export default SetupYourHprId;
